// Session AHB: the shared chrome for an office list rendered as CARDS below md.
//
// WHY THIS EXISTS. Sessions AGY and AGZ gave the office list tables priority columns
// (`max-md:hidden` on the columns that matter least) and a mobile disclosure chevron. That
// halves the problem and leaves the other half: the SURVIVING columns still divide one phone
// width between them, so a fixed-layout `.ele-list-table` truncates them all. Measured at
// 375px, the Invoices list gives the customer 144px, the due date 54px and the status 41px --
// which is why the Director's screenshot shows a clipped name, a one-word date and an
// unreadable status. Ten pages carried the same shape. More column hiding cannot fix it (the
// columns left ARE the ones you need); a card can, because it stacks instead of dividing.
//
// The card is deliberately a shared component and not ten hand-rolled markup blocks: the
// pages differ in WHAT they show, never in how a card behaves. Everything load-bearing lives
// here once -- the tap target, the 60px floor, the untruncated primary line, the actions row
// and (below) the overflow rule that a previous session had to learn the hard way.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export function MobileCardList({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <ul className="space-y-3" aria-label={label}>
      {children}
    </ul>
  );
}

export function MobileListCard({
  to,
  href,
  onClick,
  primary,
  secondary,
  meta,
  right,
  details,
  actions,
  ariaLabel,
}: {
  // Where the whole card taps through to. Omit ALL THREE for a row that has nowhere to go
  // (a draft quote has no accept token yet): the card then renders as plain content rather
  // than a control that looks tappable and does nothing.
  //   to      -- an in-app route (react-router Link)
  //   href    -- an external URL, opened in a new tab exactly as the desktop row does
  //   onClick -- an in-place action, typically expanding the row
  to?: string;
  href?: string;
  onClick?: () => void;
  // Line 1. The identifier the office scans for, and the one thing that must never truncate.
  primary: ReactNode;
  // Line 2, muted: the site, the reference, the description.
  secondary?: ReactNode;
  // Line 3, muted and smaller: the date, formatted "9 Jun 2026" and never an ISO string.
  meta?: ReactNode;
  // The right rail: status pill, amount, badge. Stacks top-down, right-aligned.
  right?: ReactNode;
  // Anything that belongs to the row but not on its three lines: an error reason, or the
  // content an expanded row discloses. Rendered in its own hairline-separated block under
  // the body, so it is readable at full card width rather than squeezed into a column.
  details?: ReactNode;
  // Controls, on their own row UNDER the tap-through body. They are a SIBLING of the link,
  // never a child: a <button> or <a> nested inside a <Link> is invalid HTML and swallows the
  // inner control's click. Anything placed here must carry its own 44px tap target.
  actions?: ReactNode;
  ariaLabel?: string;
}) {
  const body = (
    <>
      <span className="min-w-0 flex-1">
        {/* break-words, never truncate: an untruncated primary line is the entire point of
            the card. A long customer name wraps and grows the card instead of ellipsising. */}
        <span className="block break-words font-semibold text-ele-strong">{primary}</span>
        {secondary != null && <span className="mt-0.5 block break-words text-sm text-ele-body">{secondary}</span>}
        {/* break-words here too, and it is not decoration: MEASURED at 320px, a long unbroken
            token on this line overflows its box by 147px and paints over the right rail (the
            AEW class), because overflow-wrap defaults to `normal` and the box is
            overflow:visible. The lines this card is given today are mostly dates, but one is a
            free-text borough, and a shared primitive has to hold for the caller after next. */}
        {meta != null && <span className="mt-0.5 block break-words text-xs text-ele-body opacity-70">{meta}</span>}
      </span>
      {right != null && <span className="flex max-w-[50%] flex-none flex-col items-end gap-1.5 text-right">{right}</span>}
    </>
  );
  // 60px floor per the brief, comfortably over the 44px tap-target rule.
  const bodyClass = "flex min-h-[60px] w-full items-start justify-between gap-3 px-4 py-3 text-left";

  return (
    // NO overflow-hidden on this wrapper. It was an adversarial-review HIGH in Session AGZ:
    // an absolutely-positioned dropdown inside a card (the record panel's More menu) is
    // clipped by an overflow-hidden ancestor into an untappable sliver, and z-index cannot
    // beat an ancestor clip. The class only ever rounded the corners, which the border
    // already does. Do not add it back.
    <li className="rounded-ele border border-ele-purple-accent bg-ele-surface">
      {to ? (
        // A Link, not a click handler on a div: it keeps keyboard focus, open-in-new-tab and
        // the browser's own status bar, which the desktop table rows already offer.
        <Link to={to} className={bodyClass} aria-label={ariaLabel}>
          {body}
        </Link>
      ) : href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={bodyClass} aria-label={ariaLabel}>
          {body}
        </a>
      ) : onClick ? (
        <button type="button" onClick={onClick} className={bodyClass} aria-label={ariaLabel}>
          {body}
        </button>
      ) : (
        <div className={bodyClass}>{body}</div>
      )}
      {details != null && <div className="border-t border-ele-purple-accent px-4 py-3">{details}</div>}
      {actions != null && (
        <div className="flex flex-wrap items-center gap-2 border-t border-ele-purple-accent px-4 py-2">{actions}</div>
      )}
    </li>
  );
}
